import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { ResponseError, ThunkConfig } from "../../types/responseType";
import {
  Student,
  StudentResponse,
  StudentParams,
} from "../../types/studentType";
import StudentApis from "../../api/studentApis";

interface StudentState {
  students: Student[];
  groupStudents: Student[];
  currentStudent: Student | null;
  isLoading: boolean;
  isAdding: boolean;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
  };
  searchFilters: {
    studentName: string;
    parentContact: string;
    groupId: string;
    academicCycleId: string;
    classId: string;
    rollNo: string;
    studentAdmissionSequence: string;
    className: string;
  };
}

const initialState: StudentState = {
  students: [],
  groupStudents: [],
  currentStudent: null,
  isLoading: false,
  isAdding: false,
  pagination: {
    currentPage: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0,
  },
  searchFilters: {
    studentName: "",
    parentContact: "",
    groupId: "",
    academicCycleId: "",
    classId: "",
    rollNo: "",
    studentAdmissionSequence: "",
    className: "",
  },
};

export const getStudents = createAsyncThunk<
  StudentResponse,
  StudentParams,
  ThunkConfig
>("student/getStudents", async (params, thunkAPI) => {
  try {
    const response = await StudentApis.getStudents(params);
    return response?.data as StudentResponse;
  } catch (error: ResponseError | any) {
    const message =
      error?.response?.data?.error?.message || "Error fetching students";
    return thunkAPI.rejectWithValue(message);
  }
});

export const getStudentsByGroup = createAsyncThunk<
  StudentResponse,
  StudentParams,
  ThunkConfig
>("student/getStudentsByGroup", async (params, thunkAPI) => {
  try {
    const response = await StudentApis.getStudents(params);
    return response?.data as StudentResponse;
  } catch (error: ResponseError | any) {
    const message =
      error?.response?.data?.error?.message || "Error fetching group students";
    return thunkAPI.rejectWithValue(message);
  }
});

// ================== Slice ==================
const studentSlice = createSlice({
  name: "student",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSearchFilters: (
      state,
      action: PayloadAction<Partial<StudentState["searchFilters"]>>,
    ) => {
      state.searchFilters = { ...state.searchFilters, ...action.payload };
    },
    clearSearchFilters: (state) => {
      state.searchFilters = initialState.searchFilters;
    },
    setPagination: (
      state,
      action: PayloadAction<{
        currentPage: number;
        pageSize: number;
      }>,
    ) => {
      state.pagination.currentPage = action.payload.currentPage;
      state.pagination.pageSize = action.payload.pageSize;
    },
    clearStudents: (state) => {
      state.students = [];
      state.pagination = initialState.pagination;
    },
    clearCurrentStudent: (state) => {
      state.currentStudent = null;
    },
    resetStudentState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStudents.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getStudents.fulfilled, (state, action) => {
        state.students = action.payload.content;
        state.pagination = {
          currentPage: action.payload.number,
          pageSize: action.payload.size,
          totalElements: action.payload.totalElements,
          totalPages: action.payload.totalPages,
        };
        state.isLoading = false;
      })
      .addCase(getStudents.rejected, (state, { payload }) => {
        toast.error(payload as string);
        state.isLoading = false;
        state.students = [];
      })
      .addCase(getStudentsByGroup.fulfilled, (state, action) => {
        state.groupStudents = action.payload.content;
      });
  },
});

export const {
  setLoading,
  setSearchFilters,
  setPagination,
  clearStudents,
  clearCurrentStudent,
  resetStudentState,
  clearSearchFilters,
} = studentSlice.actions;

export default studentSlice.reducer;
